import React, { useState, useEffect, useRef } from "react";
import BrandIcon from "./BrandIcon";

/**
 * 법률 AI 분석 과정을 단계별로 보여주는 Thinking Progress 컴포넌트
 * 
 * - loading=true: 펼쳐진 채로 단계가 하나씩 추가됨 (타이머 표시)
 * - loading=false: 접기/펼치기 가능, "분석 완료" 표시
 */
export default function ThinkingProgress({ loading, steps = [], interval = 2000 }) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  // 단계 순차 표시
  useEffect(() => {
    if (steps.length === 0) return;
    // 새 단계가 추가되면 바로 보이도록
    if (steps.length > visibleCount) {
      setVisibleCount(steps.length);
      return;
    }
    if (visibleCount >= steps.length) return;
    const timer = setTimeout(() => setVisibleCount((c) => c + 1), interval);
    return () => clearTimeout(timer);
  }, [steps.length, visibleCount, interval]);

  // 경과 시간
  useEffect(() => {
    if (!loading) return;
    startRef.current = Date.now();
    const iv = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [loading]);

  // 완료되면 자동 접기
  useEffect(() => {
    if (!loading && steps.length > 0) {
      const timer = setTimeout(() => setCollapsed(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, steps.length]);

  const displayed = steps.slice(0, visibleCount);
  if (displayed.length === 0 && !loading) return null;

  const isDoneStep = (step) => step.startsWith("[done]");
  const stepLabel = (step) => step.replace(/^\[done\]\s*/, "");

  return (
    <div className="mb-3">
      {/* 헤더 */}
      <button
        onClick={() => !loading && setCollapsed((c) => !c)}
        className={`flex items-center gap-2 text-xs font-medium w-full text-left py-1.5 ${
          loading ? "text-[#9b6f45]" : "text-[#8a7968] hover:text-[#5f554b] cursor-pointer"
        }`}
      >
        {loading ? (
          <span className="inline-block w-3.5 h-3.5 border-2 border-[#9b6f45] border-t-transparent rounded-full animate-spin" />
        ) : (
          <BrandIcon name="check" size={14} className="text-[#58664a]" />
        )}
        <span>{loading ? "분석 중..." : "분석 완료"}</span>
        {loading && (
          <span className="ml-auto text-[#8a7968] tabular-nums">{elapsed}초</span>
        )}
        {!loading && (
          <span className="ml-auto text-[#8a7968]">{collapsed ? "접기" : "열기"}</span>
        )}
      </button>

      {/* 단계 목록 */}
      {!collapsed && (
        <div className="ml-5 mt-1 space-y-1 text-xs text-[#6f6258]">
          {displayed.map((step, i) => (
            <div key={i} className="flex items-center gap-1.5 animate-fade-in">
              {isDoneStep(step) ? (
                <BrandIcon name="check" size={12} className="text-[#58664a] flex-shrink-0" />
              ) : (
                <span className="text-[#9b6f45] flex-shrink-0">-</span>
              )}
              <span>{stepLabel(step)}</span>
            </div>
          ))}
          {loading && displayed.length > 0 && (
            <div className="flex items-center gap-1.5 text-[#9b6f45]">
              <span className="flex gap-0.5">
                <span className="w-1 h-1 bg-[#9b6f45] rounded-full animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1 h-1 bg-[#9b6f45] rounded-full animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1 h-1 bg-[#9b6f45] rounded-full animate-bounce" />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
